import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import DataTable from "../components/common/Table";
import Modal, { FIELD_CLASS, LABEL_CLASS } from "../components/common/Modal";
import ConfirmModal from "../components/common/ConfirmModal";
import useStore from "../store/useStore";
import api from "../services/api";

const emptyForm = {
  assetNumber: "",
  category: "Residential",
  assetName: "",
  ownerId: "",
  description: "",
  location: "",
  rentalSpace: ""
};

const Assets = () => {
  const { toast } = useStore();

  const [assets, setAssets] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/assets");
      setAssets(res.data.data || []);
    } catch (error) {
      console.error("Error fetching assets:", error);
      toast("Failed to load assets", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchOwners = useCallback(async () => {
    try {
      const res = await api.get("/asset-owners");
      setOwners(res.data.data || []);
    } catch (error) {
      console.error("Error fetching owners:", error);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
    fetchOwners();
  }, [fetchAssets, fetchOwners]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const closeForm = () => {
    setForm(emptyForm);
    setSelectedAsset(null);
    setFormOpen(false);
  };

  const openCreate = () => {
    setForm(emptyForm);
    setSelectedAsset(null);
    setFormOpen(true);
  };

  const openEdit = (asset) => {
    setSelectedAsset(asset);
    setForm({
      assetNumber: asset.assetNumber || "",
      category: asset.category || "Residential",
      assetName: asset.assetName || "",
      ownerId: asset.ownerId?.toString() || "",
      description: asset.description || "",
      location: asset.location || "",
      rentalSpace: asset.rentalSpace?.toString() || ""
    });
    setFormOpen(true);
  };

  const handleAdd = async () => {
    if (!form.assetName || !form.assetName.trim()) {
      toast("Asset name is required", "error");
      return;
    }

    if (!form.location || !form.location.trim()) {
      toast("Location is required", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        assetName: form.assetName.trim(),
        category: form.category,
        ownerId: form.ownerId ? parseInt(form.ownerId) : null,
        description: form.description?.trim() || null,
        location: form.location.trim(),
        rentalSpace: form.rentalSpace ? parseInt(form.rentalSpace) : null
      };

      const response = await api.post("/assets", payload);
      toast(response.data.message || "Asset added successfully", "success");
      closeForm();
      fetchAssets();
    } catch (error) {
      console.error("Error adding asset:", error);
      toast(error.response?.data?.message || "Failed to add asset", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedAsset) {
      toast("Please select an asset to update", "error");
      return;
    }

    if (!form.assetName || !form.assetName.trim()) {
      toast("Asset name is required", "error");
      return;
    }

    if (!form.location || !form.location.trim()) {
      toast("Location is required", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        assetName: form.assetName.trim(),
        category: form.category,
        ownerId: form.ownerId ? parseInt(form.ownerId) : null,
        description: form.description?.trim() || null,
        location: form.location.trim(),
        rentalSpace: form.rentalSpace ? parseInt(form.rentalSpace) : null
      };

      const response = await api.put(`/assets/${selectedAsset.id}`, payload);
      toast(response.data.message || "Asset updated successfully", "success");
      closeForm();
      fetchAssets();
    } catch (error) {
      console.error("Error updating asset:", error);
      toast(error.response?.data?.message || "Failed to update asset", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setLoading(true);
      const response = await api.delete(`/assets/${deleteTarget.id}`);
      toast(response.data.message || "Asset deleted successfully", "success");
      setDeleteTarget(null);
      fetchAssets();
    } catch (error) {
      console.error("Error deleting asset:", error);
      toast(error.response?.data?.message || "Failed to delete asset", "error");
    } finally {
      setLoading(false);
    }
  };

  const filtered = assets.filter((asset) => {
    const term = search.toLowerCase();
    return (
      (asset.assetName || "").toLowerCase().includes(term) ||
      (asset.location || "").toLowerCase().includes(term) ||
      (asset.category || "").toLowerCase().includes(term) ||
      (asset.ownerName || "").toLowerCase().includes(term) ||
      (asset.assetNumber || "").toLowerCase().includes(term)
    );
  });

  return (
    <div>
      <PageHeader
        title="Assets"
        action={
          <Button variant="primary" icon="add" onClick={openCreate}>
            Add Asset
          </Button>
        }
      />

      <DataTable
        columns={[
          { key: "assetNumber", label: "Asset No", render: (row) => row.assetNumber || "-" },
          { key: "assetName", label: "Name" },
          { key: "category", label: "Category" },
          { key: "ownerName", label: "Owner", render: (row) => row.ownerName || "-" },
          { key: "location", label: "Location" },
          {
            key: "rentalSpace",
            label: "Units",
            sortValue: (row) => Number(row.rentalSpace || 0),
            render: (row) => row.rentalSpace || "-"
          }
        ]}
        rows={filtered}
        loading={loading && !formOpen && !deleteTarget}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchPlaceholder="Search by asset name, location, category or owner..."
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        emptyMessage='No assets found. Click "Add Asset" to create one.'
      />

      <Modal
        open={formOpen}
        title={selectedAsset ? "Update Asset" : "Add Asset"}
        onClose={closeForm}
        footer={
          <>
            <Button variant="secondary" onClick={closeForm} disabled={loading}>
              Cancel
            </Button>
            {selectedAsset ? (
              <Button variant="success" icon="update" onClick={handleUpdate} disabled={loading}>
                Update
              </Button>
            ) : (
              <Button variant="primary" icon="add" onClick={handleAdd} disabled={loading}>
                Add
              </Button>
            )}
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className={LABEL_CLASS}>Asset Number</label>
            <input
              type="text"
              name="assetNumber"
              value={form.assetNumber}
              disabled
              className={FIELD_CLASS}
              title="Asset number is auto-generated and cannot be edited"
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Category</label>
            <select name="category" value={form.category} onChange={handleChange} className={FIELD_CLASS}>
              <option value="Residential">Residential</option>
              <option value="Commercial">Commercial</option>
              <option value="Residential and Commerce">Residential and Commerce</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLASS}>
              Asset Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="assetName"
              value={form.assetName}
              onChange={handleChange}
              placeholder="Enter asset name"
              className={FIELD_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Owner Name</label>
            <select name="ownerId" value={form.ownerId} onChange={handleChange} className={FIELD_CLASS}>
              <option value="">Select owner (optional)</option>
              {owners.map((owner) => (
                <option key={owner.id} value={owner.id}>
                  {owner.firstName} {owner.lastName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLASS}>Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              placeholder="Enter description (optional)"
              className={`${FIELD_CLASS} resize-none`}
            />
          </div>
          <div className="space-y-6">
            <div>
              <label className={LABEL_CLASS}>
                Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="location"
                value={form.location}
                onChange={handleChange}
                placeholder="Enter location"
                className={FIELD_CLASS}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>Rental Space (Units)</label>
              <input
                type="number"
                name="rentalSpace"
                value={form.rentalSpace}
                onChange={handleChange}
                min="0"
                placeholder="Enter number of units (optional)"
                className={FIELD_CLASS}
              />
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        message={deleteTarget ? `Are you sure you want to delete asset "${deleteTarget.assetName}"?` : ""}
        loading={loading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Assets;
